<script lang="ts">
  import { COOLANT_REQUIRED_TICKS, DECK, HOLD_REQUIRED_TICKS, TICK_HZ, type HandleId, type NodeId } from "@spacecrew/shared";
  import { ship } from "../net/ship.svelte.js";
  import { session } from "../net/session.svelte.js";
  import { send } from "../net/intents.js";
  import { isUnloading } from "../lib/unloading.js";

  let { handleId, task: kind = "surge", verb = "SURGE DUMP" }: { handleId: HandleId; task?: "surge" | "coolant"; verb?: string } = $props();

  const snap = $derived(ship.snap!);
  const task = $derived(kind === "surge" ? snap.surge : snap.coolant);
  const handle = $derived(task.handles[handleId]!);
  const other = $derived(Object.entries(task.handles).find(([id]) => id !== handleId)!);
  const mine = $derived(handle.heldBy === session.sessionId);
  const available = $derived(snap.phase === "running" && (task.status === "ready" || task.status === "waiting") && (handle.heldBy === "" || mine));
  const cooldownS = $derived(Math.max(0, (task.untilTick - snap.tick) / TICK_HZ));
  const required = $derived(kind === "surge" ? HOLD_REQUIRED_TICKS : COOLANT_REQUIRED_TICKS);

  let pressed = $state(false);

  function press() { if (!available || pressed) return; pressed = true; send("hold.press", { task: kind, handleId }); }
  function release() { if (isUnloading()) return; if (!pressed && !mine) return; pressed = false; send("hold.release", { task: kind, handleId }); }

  function onKeyDown(e: KeyboardEvent) { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); press(); } }
  function onKeyUp(e: KeyboardEvent) { if (e.key === " " || e.key === "Enter") { e.preventDefault(); release(); } }
  function onPointerDown(e: PointerEvent) { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); press(); }

  $effect(() => {
    const onHide = () => { if (document.hidden) release(); };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  });

  const label = $derived.by(() => {
    if (task.status === "engaged") return `${verb} ${Math.round((task.heldTicks / required) * 100)} % — KEEP HOLDING`;
    if (task.status === "cooldown") return kind === "coolant" ? (task.untilTick ? `BACKFLOW — RETRY IN ${cooldownS.toFixed(0)} s` : "LOOP SEALED") : `${task.lastOutcome === "fail" ? "BACKFLOW LOCKOUT" : "RECHARGING"} ${cooldownS.toFixed(0)} s`;
    if (mine) return "HANDLE ENGAGED — RELEASE TO ABORT";
    if (handle.heldBy) return "HANDLE HELD BY ANOTHER OPERATOR";
    return `${kind === "coolant" ? "HOLD VALVE" : "HOLD HANDLE"} ${handleId}`;
  });
</script>

<div class="handle">
  <button
    aria-pressed={mine || (task.status === "engaged" && mine)}
    disabled={!available && !mine}
    onpointerdown={onPointerDown}
    onpointerup={release}
    onpointercancel={release}
    onlostpointercapture={release}
    onkeydown={onKeyDown}
    onkeyup={onKeyUp}
    oncontextmenu={(e) => e.preventDefault()}
  >{label}</button>
  <p class="dim">
    {kind === "coolant" ? "VALVE" : "HANDLE"} {other[0]} ({DECK.nodes[other[1].nodeId as NodeId].label}): {other[1].heldBy ? (other[1].heldBy === session.sessionId ? "YOU?" : (snap.crew[other[1].heldBy]?.name ?? "OPERATOR")) : "— UNATTENDED — SEND SOMEONE"}
    {#if task.status === "waiting" && mine}· AWAITING SECOND OPERATOR{/if}
  </p>
  {#if task.status === "engaged"}
    <progress max={required} value={task.heldTicks} aria-label="{verb} progress"></progress>
  {/if}
</div>

<style>
  .handle { display: grid; gap: 0.4rem; }
  button { min-height: 64px; touch-action: none; user-select: none; -webkit-user-select: none; }
  progress { width: 100%; accent-color: var(--fg); }
</style>
